import React, { useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { useAuthStore } from '../../stores/authStore'
import { useFamilyStore } from '../../stores/familyStore'
import { useUserPreferencesStore } from '../../stores/userPreferencesStore'
import { getColorScheme } from '../../utils/colorSchemes'
import { getTypeColor } from '../../utils/eventTypeColors'
import { useEntitlements } from '../../hooks/useEntitlements'
import { usePermissions } from '../../hooks/usePermissions'
import { useCalendar } from '../../hooks/useCalendar'
import { useEventsStore } from '../../stores/eventsStore'
import { useCalendarStore } from '../../stores/calendarStore'

type CombinedMode = 'care' | 'note' | 'expense'

interface CombinedModalProps {
  isOpen: boolean
  onClose: () => void
  defaultDate: Date
  childId?: string
}

// Combined Modal: Care / Note / Expense
export const CombinedModal: React.FC<CombinedModalProps> = ({ isOpen, onClose, defaultDate, childId }) => {
  const { user } = useAuthStore()
  const { family } = useFamilyStore()
  const scheme = useUserPreferencesStore(state => getColorScheme(state.preferences.color_scheme))
  const preferredCurrency = useUserPreferencesStore(state => state.preferences.preferred_currency)
  const entitlements = useEntitlements(family?.id)
  const { canEditCalendar } = usePermissions()
  const { addEvent } = useEventsStore()
  const { updateCalendarEntry } = useCalendar()
  const entries = useCalendarStore(state => state.entries)

  const [mode, setMode] = useState<CombinedMode>('care')

  // Care – local selection of new location
  const dateKey = useMemo(() => {
    const d = new Date(defaultDate)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [defaultDate])
  const existingEntry = useMemo(() => entries.find(e => e.date === dateKey), [entries, dateKey])
  const [careSelection, setCareSelection] = useState<'home_a' | 'home_b' | null>(existingEntry?.location ?? null)

  // Note/Expense – form fields (adapted from EventModal)
  const toLocalInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
  const fromLocalInput = (s: string) => new Date(s)
  const roundTo30 = (date: Date) => {
    const d = new Date(date)
    const mins = d.getMinutes()
    d.setMinutes(mins < 30 ? 30 : 60, 0, 0)
    return d
  }
  const [title, setTitle] = useState('')
  const [noteAllDay, setNoteAllDay] = useState(false)
  const [startAt, setStartAt] = useState(() => toLocalInput(roundTo30(defaultDate)))
  const [endAt, setEndAt] = useState(() => toLocalInput(new Date(roundTo30(defaultDate).getTime() + 60 * 60 * 1000)))
  const [durationMinutes, setDurationMinutes] = useState<number>(60)
  const [location, setLocation] = useState('')
  const [notes, setNotes] = useState('')
  const [amount, setAmount] = useState<number | ''>('')
  const [currency, setCurrency] = useState<'USD' | 'EUR'>(preferredCurrency || 'USD')
  const [saving, setSaving] = useState(false)

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setMode('care')
      setCareSelection(existingEntry?.location ?? null)
      const start = roundTo30(defaultDate)
      setStartAt(toLocalInput(start))
      setEndAt(toLocalInput(new Date(start.getTime() + 60 * 60 * 1000)))
      setDurationMinutes(60)
      setTitle('')
      setLocation('')
      setNotes('')
      setAmount('')
      setCurrency(preferredCurrency || 'USD')
      setNoteAllDay(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultDate])

  // Sync endAt with duration when note mode and not all day
  useEffect(() => {
    if (mode === 'note' && !noteAllDay && startAt && durationMinutes) {
      const startDate = fromLocalInput(startAt)
      const newEnd = new Date(startDate.getTime() + durationMinutes * 60000)
      setEndAt(toLocalInput(newEnd))
    }
  }, [mode, noteAllDay, startAt, durationMinutes])

  if (!isOpen) return null

  const disabledEventsAdd = !entitlements.canAddEvents || !canEditCalendar || !user || !family || !childId || childId === 'all-children'
  const disabledCare = !canEditCalendar

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !family) return

    setSaving(true)
    try {
      if (mode === 'care') {
        // Change care assignment for the given day
        await updateCalendarEntry(dateKey, 'day', careSelection)
        onClose()
        return
      }

      if (mode === 'expense') {
        if (disabledEventsAdd) return
        if (amount === '' || Number(amount) <= 0) { setSaving(false); return }
        await addEvent({
          family_id: family.id,
          child_id: childId!,
          title: title.trim() || 'Expense',
          notes: notes?.trim() || null,
          start_at: fromLocalInput(startAt).toISOString() as any,
          end_at: null as any,
          all_day: false,
          location: location?.trim() || null,
          type: 'expense',
          amount: Number(amount),
          currency,
          metadata: {} as any,
          created_by: user.id,
        } as any)
        onClose()
        return
      }

      // note
      if (disabledEventsAdd) return
      await addEvent({
        family_id: family.id,
        child_id: childId!,
        title: title.trim() || 'Note',
        notes: notes?.trim() || null,
        start_at: fromLocalInput(startAt).toISOString() as any,
        end_at: noteAllDay ? null as any : fromLocalInput(endAt).toISOString() as any,
        all_day: noteAllDay,
        location: location?.trim() || null,
        type: 'note',
        amount: null as any,
        currency: 'USD' as const,
        metadata: {} as any,
        created_by: user.id,
      } as any)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 p-2 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border border-slate-200 mt-[20vh]">
        {/* Top actions */}
        <div className="flex items-center justify-end px-4 pt-3">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm font-medium rounded-md border-2 border-red-500 text-red-600 bg-white hover:bg-red-50 transition-colors mr-2">Cancel</button>
          <button type="submit" form="combined-form" disabled={saving || (mode !== 'care' && disabledEventsAdd) || (mode === 'care' && disabledCare)} title={(mode !== 'care' && disabledEventsAdd) ? 'Available in Pro plan or select a child' : (mode === 'care' && disabledCare ? 'No permission to edit calendar' : undefined)} className="btn btn-sm btn-primary disabled:opacity-60" style={{ width: '90px' }}>{saving ? 'Saving...' : (mode === 'care' ? 'Change' : 'Add')}</button>
        </div>

        {/* Mode switch */}
        <div className="px-4 pb-3 mt-3 border-b">
          <div className="grid grid-cols-3 gap-2">
            {(['care', 'note', 'expense'] as const).map(m => {
              const active = mode === m
              const color = m === 'care' ? '#0ea5e9' : getTypeColor(m === 'note' ? 'note' : 'expense', scheme.id)
              const text = active ? 'text-white' : 'text-gray-700'
              const border = active ? 'border-2 border-gray-300' : 'border border-gray-200'
              const label = m === 'care' ? 'Care' : m === 'note' ? 'Note' : 'Expense'
              const gated = (m === 'note' || m === 'expense') && !entitlements.canAddEvents
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`relative w-full py-2 text-sm font-medium rounded-lg transition-all duration-200 ${text} ${border} ${active ? 'shadow-sm' : 'hover:bg-gray-50'}`}
                  style={{ backgroundColor: active ? color : undefined }}
                >
                  {label}
                  {gated && (
                    <span className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-500 text-white border border-sky-600">Pro</span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <form id="combined-form" onSubmit={handleSubmit} className="px-4 py-3">
          {mode === 'care' ? (
            <div className="space-y-3">
              <div className="text-sm text-slate-700">Day: {format(defaultDate, 'dd MMM yyyy')}</div>
              <div className="flex items-stretch gap-2 justify-start">
                <button
                  type="button"
                  onClick={() => setCareSelection('home_a')}
                  className={`w-1/3 px-3 py-2 rounded-lg text-sm font-medium border text-white ${careSelection === 'home_a' ? 'shadow-md border-2 ring-2 ring-blue-900' : 'opacity-60 hover:opacity-80 border-white/30'}`}
                  style={{ backgroundColor: getColorScheme(useUserPreferencesStore.getState().preferences.color_scheme).homeA.color, borderColor: careSelection === 'home_a' ? getColorScheme(useUserPreferencesStore.getState().preferences.color_scheme).homeA.color : undefined }}
                >
                  {family?.home_a_name || 'Home A'}
                </button>
                <button
                  type="button"
                  onClick={() => setCareSelection('home_b')}
                  className={`w-1/3 px-3 py-2 rounded-lg text-sm font-medium border text-white ${careSelection === 'home_b' ? 'shadow-md border-2 ring-2 ring-blue-900' : 'opacity-60 hover:opacity-80 border-white/30'}`}
                  style={{ backgroundColor: getColorScheme(useUserPreferencesStore.getState().preferences.color_scheme).homeB.color, borderColor: careSelection === 'home_b' ? getColorScheme(useUserPreferencesStore.getState().preferences.color_scheme).homeB.color : undefined }}
                >
                  {family?.home_b_name || 'Home B'}
                </button>
                <button type="button" onClick={() => setCareSelection(null)} className={`w-1/3 px-3 py-2 rounded-lg text-sm font-medium border ${careSelection === null ? 'border-blue-600 ring-2 ring-blue-400' : 'border-slate-300 text-slate-700'} `}>
                  Unassigned
                </button>
              </div>
              {!canEditCalendar && (
                <div className="mt-1 p-2 rounded bg-slate-100 border text-xs text-slate-600">No permission to change care.</div>
              )}
            </div>
          ) : mode === 'note' ? (
            <div>
              <div>
                <label className="block text-xs font-medium mb-1 text-slate-700">Title</label>
                <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div>
                  <label className="block text-xs font-medium mb-1 text-slate-700">From</label>
                  <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs md:text-sm leading-tight" required />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-slate-700">Duration</label>
                  <select value={durationMinutes} onChange={e => setDurationMinutes(Number(e.target.value))} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" disabled={noteAllDay} required={!noteAllDay}>
                    <option value={30}>30 min</option>
                    <option value={60}>1 hr</option>
                    <option value={120}>2 hrs</option>
                    <option value={180}>3 hrs</option>
                    <option value={240}>4 hrs</option>
                    <option value={300}>5 hrs</option>
                    <option value={360}>6 hrs</option>
                    <option value={420}>7 hrs</option>
                    <option value={480}>8 hrs</option>
                    <option value={540}>9 hrs</option>
                    <option value={600}>10 hrs</option>
                    <option value={660}>11 hrs</option>
                    <option value={720}>12 hrs</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <input id="allday" type="checkbox" checked={noteAllDay} onChange={e => setNoteAllDay(e.target.checked)} />
                <label htmlFor="allday" className="text-sm">All day</label>
              </div>
              <div className="mt-3">
                <label className="block text-xs font-medium mb-1 text-slate-700">Location</label>
                <input value={location} onChange={e => setLocation(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </div>
              <div className="mt-3">
                <label className="block text-xs font-medium mb-1 text-slate-700">Note</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </div>
            </div>
          ) : (
            <div>
              <div>
                <label className="block text-xs font-medium mb-1 text-slate-700">Title</label>
                <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div>
                  <label className="block text-xs font-medium mb-1 text-slate-700">Date</label>
                  <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs md:text-sm leading-tight" required />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-slate-700">Amount</label>
                  <div className="flex gap-2">
                    <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value === '' ? '' : Number(e.target.value))} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
                    <select value={currency} onChange={e => setCurrency(e.target.value as 'USD' | 'EUR')} className="px-2 py-2 rounded-md border border-slate-300 focus:outline-none">
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-xs font-medium mb-1 text-slate-700">Note</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-400" />
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default CombinedModal
